import {
  type AbleitungsmodifikationenRepraesentation,
  type AbleitungsmodifikationenStruktur,
  type Anzahl,
  type SchemaElementArt,
  parseAbleitungsmodifikationenRepraesentation,
  parseAbleitungsmodifikationenStruktur,
  parseAnzahl,
  parseSchemaElementArt,
  parseXmlDocument,
} from "../common";
import { InternalParserError } from "../errors";
import { Table } from "../table";
import { parseCode, parseDate } from "../util";
import {
  type BaseData,
  type CodelisteReferenz,
  type Datenfeld,
  type Datenfeldgruppe,
  type Datentyp,
  type ElementData,
  type ElementReference,
  type ElementStatus,
  type Feldart,
  type GenericodeIdentification,
  type Regel,
  SchemaContainer,
  type Stammdatenschema,
  parseDatentyp,
  parseElementStatus,
  parseFeldart,
} from "./schema";

export interface Context {
  datenfeldgruppen: Table<Datenfeldgruppe>;
  datenfelder: Table<Datenfeld>;
  regeln: Table<Regel>;
}

// Helper function to get text content from an element
function getTextContent(element: Element): string | undefined {
  const text = element.textContent;

  if (text === null || text.trim() === "") {
    return undefined;
  } else {
    return text;
  }
}

// Parse identification element
function parseIdentification(element: Element): [string, string?] {
  let id: string | undefined;
  let version: string | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "id":
        id = getTextContent(child);
        break;
      case "version":
        version = getTextContent(child);
        break;
    }
  }

  if (!id) {
    throw InternalParserError.missingValue("id");
  }

  return [id, version];
}

// Parse genericode identification
function parseGenericodeIdentification(
  element: Element,
): GenericodeIdentification {
  let canonicalIdentification: string | undefined;
  let version: string | undefined;
  let canonicalVersionUri: string | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "canonicalIdentification":
        canonicalIdentification = getTextContent(child);
        break;
      case "version":
        version = getTextContent(child);
        break;
      case "canonicalVersionUri":
        canonicalVersionUri = getTextContent(child);
        break;
    }
  }

  if (!canonicalIdentification) {
    throw InternalParserError.missingValue("canonicalIdentification");
  }
  if (!canonicalVersionUri) {
    throw InternalParserError.missingValue("canonicalVersionUri");
  }

  return {
    canonicalIdentification,
    version,
    canonicalVersionUri,
  };
}

// Parse base data
function parseBaseData(element: Element): BaseData {
  let id: string | undefined;
  let version: string | undefined;
  let name: string | undefined;
  let bezeichnungEingabe: string | undefined;
  let bezeichnungAusgabe: string | undefined;
  let beschreibung: string | undefined;
  let definition: string | undefined;
  let bezug: string | undefined;
  let status: ElementStatus | undefined;
  let gueltigAb: Date | undefined;
  let gueltigBis: Date | undefined;
  let fachlicherErsteller: string | undefined;
  let versionshinweis: string | undefined;
  let freigabedatum: Date | undefined;
  let veroeffentlichungsdatum: Date | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "identifikation":
        [id, version] = parseIdentification(child);
        break;
      case "name":
        name = getTextContent(child);
        break;
      case "bezeichnungEingabe":
        bezeichnungEingabe = getTextContent(child);
        break;
      case "bezeichnungAusgabe":
        bezeichnungAusgabe = getTextContent(child);
        break;
      case "beschreibung":
        beschreibung = getTextContent(child);
        break;
      case "definition":
        definition = getTextContent(child);
        break;
      case "bezug":
        bezug = getTextContent(child);
        break;
      case "status":
        status = parseCode(child, parseElementStatus);
        break;
      case "gueltigAb":
        {
          const gueltigAbText = getTextContent(child);
          if (gueltigAbText) {
            gueltigAb = parseDate(gueltigAbText);
          }
        }
        break;
      case "gueltigBis":
        {
          const gueltigBisText = getTextContent(child);
          if (gueltigBisText) {
            gueltigBis = parseDate(gueltigBisText);
          }
        }
        break;
      case "fachlicherErsteller":
        fachlicherErsteller = getTextContent(child);
        break;
      case "versionshinweis":
        versionshinweis = getTextContent(child);
        break;
      case "freigabedatum":
        {
          const freigabedatumText = getTextContent(child);
          if (freigabedatumText) {
            freigabedatum = parseDate(freigabedatumText);
          }
        }
        break;
      case "veroeffentlichungsdatum":
        {
          const veroeffentlichungsdatumText = getTextContent(child);
          if (veroeffentlichungsdatumText) {
            veroeffentlichungsdatum = parseDate(veroeffentlichungsdatumText);
          }
        }
        break;
    }
  }

  if (!id || !version) {
    throw InternalParserError.missingValue("identifikation");
  }
  if (!name) {
    throw InternalParserError.missingValue("name");
  }
  if (!status) {
    throw InternalParserError.missingValue("status");
  }

  return {
    identifier: `${id}:${version}`,
    id,
    version,
    name,
    bezeichnungEingabe,
    bezeichnungAusgabe,
    beschreibung,
    definition,
    status,
    bezug,
    gueltigAb,
    gueltigBis,
    fachlicherErsteller,
    versionshinweis,
    freigabedatum,
    veroeffentlichungsdatum,
  };
}

// Parse element data
function parseElementData(element: Element): ElementData {
  const baseData = parseBaseData(element);
  let schemaelementart: SchemaElementArt | undefined;
  let hilfetextEingabe: string | undefined;
  let hilfetextAusgabe: string | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "schemaelementart":
        schemaelementart = parseCode(child, parseSchemaElementArt);
        break;
      case "hilfetextEingabe":
        hilfetextEingabe = getTextContent(child);
        break;
      case "hilfetextAusgabe":
        hilfetextAusgabe = getTextContent(child);
        break;
    }
  }

  if (!schemaelementart) {
    throw InternalParserError.missingValue("schemaelementart");
  }

  return {
    ...baseData,
    schemaelementart,
    hilfetextEingabe,
    hilfetextAusgabe,
  };
}

// Parse code list reference
function parseCodeListReference(element: Element): CodelisteReferenz {
  let id: string | undefined;
  let version: string | undefined;
  let genericode: GenericodeIdentification | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "identifikation":
        [id, version] = parseIdentification(child);
        break;
      case "genericodeIdentification":
        genericode = parseGenericodeIdentification(child);
        break;
    }
  }

  if (!id) {
    throw InternalParserError.missingValue("identifikation");
  }
  if (!genericode) {
    throw InternalParserError.missingValue("genericodeIdentification");
  }

  return {
    id,
    version,
    genericode,
  };
}

// Parse header
function parseHeader(element: Element): [string, Date] {
  let messageId: string | undefined;
  let createdAt: Date | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "nachrichtID":
        messageId = getTextContent(child);
        break;
      case "erstellungszeitpunkt":
        {
          const text = child.textContent;
          if (text !== null) {
            createdAt = parseDate(text);
          }
        }
        break;
    }
  }

  if (!messageId) {
    throw InternalParserError.missingValue("nachrichtID");
  }
  if (!createdAt) {
    throw InternalParserError.missingValue("erstellungszeitpunkt");
  }

  return [messageId, createdAt];
}

// Parse rule
function parseRule(element: Element, context: Context): string {
  const baseData = parseBaseData(element);
  let script: string | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    if (child.localName === "script") {
      script = getTextContent(child);
    }
  }

  const rule: Regel = {
    ...baseData,
    script,
  };

  context.regeln.insert(rule);
  return rule.identifier;
}

// Parse element reference
function parseElementReference(
  element: Element,
  context: Context,
): ElementReference {
  let anzahl: Anzahl | undefined;
  let bezug: string | undefined;
  let elementType: "dataField" | "dataGroup" | undefined;
  let identifier: string | undefined;

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "anzahl":
        {
          const text = child.textContent;
          if (text !== null) {
            anzahl = parseAnzahl(text);
          }
        }
        break;
      case "bezug":
        bezug = getTextContent(child);
        break;
      case "enthaelt":
        {
          const enthaeltElement = child;
          for (let j = 0; j < enthaeltElement.children.length; j++) {
            const enthaeltChild = enthaeltElement.children[j];
            switch (enthaeltChild.localName) {
              case "datenfeld":
                {
                  elementType = "dataField";
                  const datenfeld = parseDatenfeld(enthaeltChild, context);
                  identifier = datenfeld.identifier;
                }
                break;
              case "datenfeldgruppe":
                {
                  elementType = "dataGroup";
                  const datenfeldgruppe = parseDatengruppeElement(
                    enthaeltChild,
                    context,
                  );
                  identifier = datenfeldgruppe.identifier;
                }
                break;
            }
          }
        }
        break;
    }
  }

  if (!anzahl) {
    throw InternalParserError.missingValue("anzahl");
  }
  if (!elementType || !identifier) {
    throw InternalParserError.missingValue("datenfeld | datenfeldgruppe");
  }

  return {
    type: elementType,
    identifier,
    anzahl,
    bezug,
  };
}

// Parse datenfeld
function parseDatenfeld(element: Element, context: Context): Datenfeld {
  const elementData = parseElementData(element);
  let feldart: Feldart | undefined;
  let datentyp: Datentyp | undefined;
  let praezisierung: string | undefined;
  let inhalt: string | undefined;
  let codelisteReferenz: CodelisteReferenz | undefined;
  const regeln: string[] = [];

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "feldart":
        feldart = parseCode(child, parseFeldart);
        break;
      case "datentyp":
        datentyp = parseCode(child, parseDatentyp);
        break;
      case "praezisierung":
        praezisierung = getTextContent(child);
        break;
      case "inhalt":
        inhalt = getTextContent(child);
        break;
      case "codelisteReferenz":
        codelisteReferenz = parseCodeListReference(child);
        break;
      case "regel":
        regeln.push(parseRule(child, context));
        break;
    }
  }

  if (!feldart) {
    throw InternalParserError.missingValue("feldart");
  }
  if (!datentyp) {
    throw InternalParserError.missingValue("datentyp");
  }

  const datenfeld: Datenfeld = {
    ...elementData,
    feldart,
    datentyp,
    praezisierung,
    inhalt,
    codelisteReferenz,
    regeln,
  };

  context.datenfelder.insert(datenfeld);
  return datenfeld;
}

// Parse datenfeldgruppe
function parseDatengruppeElement(
  element: Element,
  context: Context,
): Datenfeldgruppe {
  const elementData = parseElementData(element);
  const regeln: string[] = [];
  const elemente: ElementReference[] = [];

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "regel":
        regeln.push(parseRule(child, context));
        break;
      case "struktur":
        elemente.push(parseElementReference(child, context));
        break;
    }
  }

  const datenfeldgruppe: Datenfeldgruppe = {
    ...elementData,
    regeln,
    elemente,
  };

  context.datenfeldgruppen.insert(datenfeldgruppe);
  return datenfeldgruppe;
}

// Parse stammdatenschema
function parseStammdatenschema(
  element: Element,
  context: Context,
): Stammdatenschema {
  const baseData = parseBaseData(element);
  let hilfetext: string | undefined;
  let ableitungsmodifikationenStruktur:
    | AbleitungsmodifikationenStruktur
    | undefined;
  let ableitungsmodifikationenRepraesentation:
    | AbleitungsmodifikationenRepraesentation
    | undefined;
  const regeln: string[] = [];
  const elemente: ElementReference[] = [];

  for (let i = 0; i < element.children.length; i++) {
    const child = element.children[i];
    switch (child.localName) {
      case "hilfetext":
        hilfetext = getTextContent(child);
        break;
      case "ableitungsmodifikationenStruktur":
        ableitungsmodifikationenStruktur = parseCode(
          child,
          parseAbleitungsmodifikationenStruktur,
        );
        break;
      case "ableitungsmodifikationenRepraesentation":
        ableitungsmodifikationenRepraesentation = parseCode(
          child,
          parseAbleitungsmodifikationenRepraesentation,
        );
        break;
      case "regel":
        regeln.push(parseRule(child, context));
        break;
      case "struktur":
        elemente.push(parseElementReference(child, context));
        break;
    }
  }

  if (!ableitungsmodifikationenStruktur) {
    throw InternalParserError.missingValue("ableitungsmodifikationenStruktur");
  }
  if (!ableitungsmodifikationenRepraesentation) {
    throw InternalParserError.missingValue(
      "ableitungsmodifikationenRepraesentation",
    );
  }

  return {
    ...baseData,
    hilfetext,
    ableitungsmodifikationenStruktur,
    ableitungsmodifikationenRepraesentation,
    regeln,
    elemente,
  };
}

export function parseFieldMessage(data: string) {
  const xmlDoc = parseXmlDocument(data);
  const rootElement = xmlDoc.documentElement;

  if (rootElement.localName !== "xdatenfelder.datenfeld.0104") {
    throw InternalParserError.unexpectedNode(rootElement.localName);
  }

  const context: Context = {
    datenfeldgruppen: new Table(),
    datenfelder: new Table(),
    regeln: new Table(),
  };

  let messageId: string | undefined;
  let createdAt: Date | undefined;
  let field: Datenfeld | undefined;

  for (let i = 0; i < rootElement.children.length; i++) {
    const child = rootElement.children[i];
    switch (child.localName) {
      case "header":
        [messageId, createdAt] = parseHeader(child);
        break;
      case "datenfeld":
        field = parseDatenfeld(child, context);
        break;
    }
  }

  if (!messageId || !createdAt) {
    throw InternalParserError.missingValue("header");
  }
  if (!field) {
    throw InternalParserError.missingValue("datenfeld");
  }

  return {
    messageId,
    createdAt,
    field,
    regeln: context.regeln,
  };
}

export function parseGroupMessage(data: string) {
  const xmlDoc = parseXmlDocument(data);
  const rootElement = xmlDoc.documentElement;

  if (rootElement.localName !== "xdatenfelder.datenfeldgruppe.0103") {
    throw InternalParserError.unexpectedNode(rootElement.localName);
  }

  const context: Context = {
    datenfeldgruppen: new Table(),
    datenfelder: new Table(),
    regeln: new Table(),
  };

  let messageId: string | undefined;
  let createdAt: Date | undefined;
  let rootIdentifier: string | undefined;

  for (let i = 0; i < rootElement.children.length; i++) {
    const child = rootElement.children[i];
    switch (child.localName) {
      case "header":
        [messageId, createdAt] = parseHeader(child);
        break;
      case "datenfeldgruppe":
        {
          const group = parseDatengruppeElement(child, context);
          rootIdentifier = group.identifier;
        }
        break;
    }
  }

  if (!messageId || !createdAt) {
    throw InternalParserError.missingValue("header");
  }
  if (!rootIdentifier) {
    throw InternalParserError.missingValue("datenfeldgruppe");
  }

  return {
    messageId,
    createdAt,
    root: rootIdentifier,
    groups: context.datenfeldgruppen,
    fields: context.datenfelder,
    rules: context.regeln,
  };
}

export function parseSchemaMessage(data: string) {
  const xmlDoc = parseXmlDocument(data);
  const rootElement = xmlDoc.documentElement;

  if (rootElement.localName !== "xdatenfelder.stammdatenschema.0102") {
    throw InternalParserError.unexpectedNode(rootElement.localName);
  }

  let messageId: string | undefined;
  let createdAt: Date | undefined;
  let schema: Stammdatenschema | undefined;

  const context: Context = {
    datenfeldgruppen: new Table(),
    datenfelder: new Table(),
    regeln: new Table(),
  };

  for (let i = 0; i < rootElement.children.length; i++) {
    const child = rootElement.children[i];
    switch (child.localName) {
      case "header":
        [messageId, createdAt] = parseHeader(child);
        break;
      case "stammdatenschema":
        schema = parseStammdatenschema(child, context);
        break;
    }
  }

  if (!messageId || !createdAt) {
    throw InternalParserError.missingValue("header");
  }
  if (!schema) {
    throw InternalParserError.missingValue("stammdatenschema");
  }

  const schemaContainer = new SchemaContainer(
    schema,
    context.datenfeldgruppen,
    context.datenfelder,
    context.regeln,
  );

  return { messageId, createdAt, schemaContainer };
}
